import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTest } from '../contexts/Test';
import { useAuthContext } from '../../common/contexts/auth';
import { mapAxeDevtoolsTestRecordIssue } from '@deque/ui-issues-mapper';
import PageTitle from '../../common/components/PageTitle';
import { Loader, Pagination } from '@deque/cauldron-react';
import ErrorMessage from '../../app/components/issue/ErrorMessage';
import { IssuesDataTable, IssueColumns, IssueFilters } from '@deque/ui-issues';
import styles from './IssuesV2.css';
import usePagination from '../../common/hooks/usePagination';

const Issues = () => {
  const { t } = useTranslation();
  const { loading: authLoading, error: authError } = useAuthContext();
  const { loading, error, test, issues } = useTest();

  const launchpadLikeIssues = useMemo(
    () =>
      issues.map(issue =>
        mapAxeDevtoolsTestRecordIssue(issue, {
          href: `${window.location.origin}/issues/${issue.issueId}`
        })
      ),
    [issues]
  );

  const issuesCount = launchpadLikeIssues.length;

  const pagination = usePagination({
    items: launchpadLikeIssues,
    initialPage: 1,
    itemsPerPage: 10,
    totalItems: issuesCount
  });
  const itemStart =
    pagination.currentPage * pagination.itemsPerPage -
    pagination.itemsPerPage +
    1;
  const itemEnd = Math.min(
    itemStart + pagination.itemsPerPage - 1,
    pagination.totalItems
  );
  const totalItems = pagination.totalItems;

  if (loading || authLoading) {
    return (
      <>
        <PageTitle title={t('Loading issues')} />
        <Loader label={t('Loading issues...')} />
      </>
    );
  }

  if (error || !test || authError) {
    return (
      <>
        <PageTitle title={t('Error loading issues')} />
        <ErrorMessage
          error={t(
            'There was an error loading your issues. Please refresh or try again later...'
          )}
        />
      </>
    );
  }

  const testName = test.name || t('Test');
  const pageTitle = t('Viewing {{testName}} issues', { testName });

  const columns = [
    { ...IssueColumns.Rule, sortable: true },
    { ...IssueColumns.Impact, sortable: true },
    { ...IssueColumns.PageInfo, sortable: true },
    { ...IssueColumns.DateTime, sortable: true }
  ];

  return (
    <div>
      <PageTitle title={pageTitle} />
      <>
        <div className={styles.issueListFiltersContainer}>
          <div className={styles.issueListFiltersContainer__filters}>
            <span>{t('Filter by:')}</span>
            <IssueFilters issues={launchpadLikeIssues} />
          </div>

          <div className={styles.issueListFiltersContainer__count}>
            {t('Showing {{fromCount}} to {{toCount}} of {{totalIssues}}', {
              fromCount: itemStart,
              toCount: itemEnd,
              totalIssues: totalItems
            })}
          </div>
        </div>
        <IssuesDataTable
          issues={pagination.currentPageItems}
          columns={columns}
        />
        <Pagination
          className={styles.paginationContainer}
          totalItems={pagination.totalItems}
          itemsPerPage={pagination.itemsPerPage}
          currentPage={pagination.currentPage}
          onNextPageClick={pagination.handleNextPage}
          onFirstPageClick={pagination.handleFirstPage}
          onPreviousPageClick={pagination.handlePreviousPage}
          onLastPageClick={pagination.handleLastPage}
        />
      </>
    </div>
  );
};

export default Issues;
